import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

import { expVal } from '@confluence/feature-experiments';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useSpaceBaseQueryBaseQuery$data,
	useSpaceBaseQueryBaseQuery$variables,
} from './__generated__/useSpaceBaseQueryBaseQuery.graphql.ts';
import type {
	useSpaceBaseQuerySingleSpaceQuery$data,
	useSpaceBaseQuerySingleSpaceQuery$variables,
} from './__generated__/useSpaceBaseQuerySingleSpaceQuery.graphql.ts';

export const useSpaceBaseQueryBaseQuery: GraphQLTaggedNode = graphql`
	query useSpaceBaseQueryBaseQuery($spaceKey: String) {
		spaces(spaceKey: $spaceKey) {
			nodes {
				id
				key
				alias
				name
				homepage {
					id
				}
				icon {
					path
					width
					height
					isDefault
				}
				type
				settings {
					routeOverrideEnabled
				}
				theme {
					themeKey
				}
				lookAndFeel {
					headings {
						key
						value
					}
					menus {
						hoverOrFocus {
							key
							value
						}
						color
					}
					header {
						backgroundColor
						button {
							backgroundColor
							color
						}
						primaryNavigation {
							color
							hoverOrFocus {
								key
								value
							}
						}
						secondaryNavigation {
							color
							hoverOrFocus {
								key
								value
							}
						}
						search {
							backgroundColor
							color
						}
					}
					content {
						screen {
							background
							backgroundColor
							backgroundImage
							backgroundPosition
							backgroundSize
							backgroundRepeat
							backgroundOrigin
							backgroundClip
							backgroundAttachment
							backgroundBlendMode
							layer {
								background
								backgroundColor
								backgroundImage
								backgroundPosition
								backgroundSize
								backgroundRepeat
								backgroundOrigin
								backgroundClip
								backgroundAttachment
								backgroundBlendMode
								width
								height
							}
							gutterTop
							gutterRight
							gutterBottom
							gutterLeft
						}
						container {
							background
							backgroundColor
							backgroundImage
							backgroundPosition
							backgroundSize
							backgroundRepeat
							backgroundOrigin
							backgroundClip
							backgroundAttachment
							backgroundBlendMode
							padding
							borderRadius
						}
						header {
							background
							backgroundColor
							backgroundImage
							backgroundPosition
							backgroundSize
							backgroundRepeat
							backgroundOrigin
							backgroundClip
							backgroundAttachment
							backgroundBlendMode
							padding
							borderRadius
						}
						body {
							background
							backgroundColor
							backgroundImage
							backgroundPosition
							backgroundSize
							backgroundRepeat
							backgroundOrigin
							backgroundClip
							backgroundAttachment
							backgroundBlendMode
							padding
							borderRadius
						}
					}
				}
				metadata {
					labels {
						nodes {
							prefix
							name
							id
						}
					}
				}
				operations {
					operation
					targetType
				}
			}
		}
	}
`;

export const useSpaceBaseQuerySingleSpaceQuery: GraphQLTaggedNode = graphql`
	query useSpaceBaseQuerySingleSpaceQuery($spaceKey: String) {
		space(key: $spaceKey) {
			id
			key
			name
			alias
		}
	}
`;

export const useSpaceBaseQuery = ({
	resolvedSpaceKey,
}: {
	resolvedSpaceKey: string;
}): {
	space:
		| {
				readonly alias: string | null;
				readonly id: string | null;
				readonly key: string | null;
				readonly name: string | null;
		  }
		| null
		| undefined;
	loading: boolean;
} => {
	const {
		data: spaceBaseQueryData,
		loading: spaceBaseQueryLoading,
		error: spaceBaseQueryError,
	} = useQueryInterop<useSpaceBaseQueryBaseQuery$data, useSpaceBaseQueryBaseQuery$variables>(
		useSpaceBaseQueryBaseQuery,
		{
			skip:
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				Boolean(process.env.REACT_SSR) ||
				expVal('confluence_p2m_style_recalc_and_expand_joint_exp', 'isEnabled', false),
			variables: { spaceKey: resolvedSpaceKey },
		},
	);

	const {
		data: singleSpaceQueryData,
		loading: singleSpaceQueryLoading,
		error: singleSpaceQueryError,
	} = useQueryInterop<
		useSpaceBaseQuerySingleSpaceQuery$data,
		useSpaceBaseQuerySingleSpaceQuery$variables
	>(useSpaceBaseQuerySingleSpaceQuery, {
		skip:
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			Boolean(process.env.REACT_SSR) ||
			!expVal('confluence_p2m_style_recalc_and_expand_joint_exp', 'isEnabled', false),
		variables: { spaceKey: resolvedSpaceKey },
	});

	let space, loading;

	if (expVal('confluence_p2m_style_recalc_and_expand_joint_exp', 'isEnabled', false)) {
		loading = singleSpaceQueryLoading;
		space = singleSpaceQueryError && !singleSpaceQueryLoading ? null : singleSpaceQueryData?.space;
	} else {
		loading = spaceBaseQueryLoading;
		space =
			spaceBaseQueryError && !spaceBaseQueryLoading ? null : spaceBaseQueryData?.spaces?.nodes?.[0];
	}

	return { space, loading };
};
